import { get, isEmpty, isUndefined } from "lodash"
import { ERROR, OK } from "helpers/data-statuses"
import { getModelDatesId, getPortfolioModelMetaUrl, getPortfolioUserPreferencesId } from "helpers/portfolio-resource-ids"
import { getCurrentPortfolio, getSelectedModelId } from "modules/portfolio-root/selectors/portfolios"
import {
  getPortfolioTradesKey,
  getTradeStorageScope,
  readSimulatorTrades,
  subscribeTradeMirror,
  type MirroredStore,
  type StoredSimulatorTrades,
} from "helpers/simulator-trade-storage"
import { getActionsBase, getRebalanceDate, getSimulateActions } from "../selectors"
import { getIntradayBatchId, getIsIntraday, getIsIntradayRebalance } from "../selectors/intraday"

export type SimulatorMode = "intraday" | "regular"

export interface PortfolioTradeContext {
  [field: string]: string | null
  userId: string | null
  accountId: string | null
  portfolioId: string | null
  mode: SimulatorMode
}

export interface PendingPortfolioTrades extends StoredSimulatorTrades {
  key: string
}

const OWNING_CONTEXT_FIELDS = ["userId", "accountId", "portfolioId", "mode"] as const

export function getTradePortfolioId(state: unknown): string | null {
  return get(state, "global.selected.portfolio") ?? null
}

function getTradeMode(state: unknown): SimulatorMode {
  const isIntradayRebalance: unknown = getIsIntradayRebalance(state)
  const isIntraday = isUndefined(isIntradayRebalance) ? getIsIntraday(state) : isIntradayRebalance
  return isIntraday ? "intraday" : "regular"
}

export function getPortfolioTradeContext(state: unknown): PortfolioTradeContext {
  const scope = getTradeStorageScope(state)
  return {
    userId: scope?.userId ?? null,
    accountId: scope?.accountId ?? null,
    portfolioId: getTradePortfolioId(state),
    mode: getTradeMode(state),
  }
}

export function getSimulatorRouteMode(pathname: string | null | undefined): SimulatorMode | null {
  const [parent, page] = (pathname ?? "").split("/").filter(Boolean).slice(-2)
  if (page !== "simulator") return null
  if (parent === "intraday") return "intraday"
  return parent === "rebalance" ? "regular" : null
}

function isSettledStatus(status: unknown): boolean {
  return status === OK || status === ERROR
}

function isResourceSettled(state: unknown, resource: string, id: string): boolean {
  const entry = get(state, ["global", "data", resource])
  return get(entry, "id") === id && isSettledStatus(get(entry, "status"))
}

export function isPortfolioTradeContextLoaded(state: unknown): boolean {
  const account: unknown = get(state, "global.selected.account")
  const accountId: unknown = get(state, "global.selected.accountId")
  const portfolioId = getTradePortfolioId(state)
  const portfolioUUID: unknown = get(getCurrentPortfolio(state), "id")
  const modelId: unknown = getSelectedModelId(state)
  if (!account || !portfolioId || !portfolioUUID || !modelId) return false
  if (!isSettledStatus(get(state, "global.data.models.status"))) return false
  return isResourceSettled(state, "portfolioUserPreferences", getPortfolioUserPreferencesId(accountId, modelId, portfolioUUID))
    && isResourceSettled(state, "portfolioModelMeta", getPortfolioModelMetaUrl(account, modelId, portfolioId))
    && isResourceSettled(state, "dates", getModelDatesId(modelId))
}

export function isPortfolioTradeContextSettled(state: unknown, stored: StoredSimulatorTrades["context"]): boolean {
  if (!isPortfolioTradeContextLoaded(state)) return false
  if (stored.mode === "intraday") return !!getIntradayBatchId(state)
  return !!getRebalanceDate(state)
}

export function readPendingPortfolioTrades(state: unknown): PendingPortfolioTrades | null {
  const portfolioId = getTradePortfolioId(state)
  const scope = getTradeStorageScope(state)
  if (!portfolioId || !scope || !isEmpty(getSimulateActions(state))) return null
  const key = getPortfolioTradesKey(scope, portfolioId)
  const stored = readSimulatorTrades(key)
  return stored ? { ...stored, key } : null
}

export function subscribePortfolioTradeMirror(store: MirroredStore<unknown>): () => void {
  return subscribeTradeMirror(store, {
    selectTrades: (state): unknown[] | undefined => getSimulateActions(state),
    selectActionsBase: (state): unknown => getActionsBase(state) ?? null,
    selectContext: getPortfolioTradeContext,
    getKey: ({ userId, accountId, portfolioId }) => (
      userId && accountId && portfolioId ? getPortfolioTradesKey({ userId, accountId }, portfolioId) : null
    ),
    owningFields: OWNING_CONTEXT_FIELDS,
  })
}
